import { ChangeDetectionStrategy, Component, inject, computed, signal, effect } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {AlertService} from '../../core/alerts/alert.service';
import { MedarbejdereFravaerPickerComponent } from '../medarbejdere-fravaer-picker/medarbejdere-fravaer-picker.component';
import { MedarbejderSelectorComponent } from '../medarbejder-selector/medarbejder-selector.component';
import { ConfirmService } from '../../shared/confirm-dialog/confirm-dialog.service';
import { IndexCardsStore } from '../indexcards.store';
import { NormtiderStore } from './normtider/normtider.store';
import { NormtiderService } from './normtider/normtider.service';
import { formatTime } from '../../shared/time';

@Component({
    selector: 'app-medarbejdere-normtider-modal-ui',
    templateUrl: './medarbejdere-normtider-modal.html',
    styleUrls: ['./medarbejdere-normtider-modal.scss'],
    providers: [NormtiderStore],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class MedarbejdereNormtiderModal {
    indexCardsStore = inject(IndexCardsStore);
    normtiderStore = inject(NormtiderStore);
    currentYear = signal(new Date().getFullYear());
    selectedMedarbejder = signal<any>(null);
    private readonly normtiderService = inject(NormtiderService);
    selectedDate = signal<number | null>(null);
    copying = signal(false);
    busy = computed(() => this.copying() || this.normtiderStore.saving());
    canEdit = computed(() => !!this.selectedMedarbejder() && !this.busy() && !this.normtiderStore.loading() && !this.normtiderStore.loadError());
    selectedDay = computed(() => this.calendar().flatMap(month => month.days).find(day => day.key === this.selectedDate()));
    selectedWeekday = computed(() => ['søndage', 'mandage', 'tirsdage', 'onsdage', 'torsdage', 'fredage', 'lørdage'][this.selectedDay()?.date.getDay()] ?? 'ugedage');
    readonly absenceTypes = [
        { code: 1, label: 'Ferie' }, { code: 2, label: 'Barsel' },
        { code: 3, label: 'Skole' }, { code: 4, label: 'Afspadsering' }
    ];

    absenceLabel(code: number): string {
        return this.absenceTypes.find(type => type.code === code)?.label ?? 'Fravær';
    }

    reload() {
        const employee = this.selectedMedarbejder();
        if (employee) { this.normtiderStore.loadForUserYear(employee.cardId, this.currentYear()); }
    }

    users = computed(() => this.indexCardsStore.users() ?? []);

    activeUsers = computed(() =>
        [...this.users()]
            .filter((u: any) => u.fratraadt === null)
            .sort((a: any, b: any) => a.cardId - b.cardId)
    );

    calendar = computed(() => {
        const entries = this.normtiderStore.entries();
        const year = this.currentYear();

        const entriesByDate = new Map(entries.map(entry => [new Date(entry.dato).toDateString(), entry]));
        const baseCalendar = this.daysInMonths(year);
        return baseCalendar.map(month => ({
            ...month,
            days: month.days.map((day: any) => {
                const match = entriesByDate.get(day.date.toDateString());

                return {
                    ...day,
                    timerValue: match ? match.timer : null,
                    kodeValue: match ? (match.kode || 0) : 0
                };
            })
        }));
    });

    constructor(
        private readonly activeModal: NgbActiveModal,
        private readonly alertService: AlertService,
        private readonly modalService: NgbModal,
        private readonly confirmService: ConfirmService
    ) {
        effect(() => {
            const users = this.activeUsers();
            const selected = this.selectedMedarbejder();

            if (!selected && users.length > 0) {
                this.selectedMedarbejder.set(users[0]);
            }
        });

        effect(() => {
            const selected = this.selectedMedarbejder();
            const year = this.currentYear();

            if (selected) {
                this.normtiderStore.loadForUserYear(selected.cardId, year);
            }
        });
    }

    daysInMonths(year: number) {
        const holidays = new Set(this.getHolidays(year).map(date => date.getTime()));
        return Array.from({ length: 12 }, (__, monthIndex) => {
            const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

            return {
                month: monthIndex,
                name: new Date(year, monthIndex).toLocaleString('da-DK', { month: 'long' }),
                days: Array.from({ length: daysInMonth }, (___, dayIndex) => {
                    const date = new Date(year, monthIndex, dayIndex + 1);

                    return {
                        date,
                        key: date.getTime(),
                        label: date.toLocaleDateString('da-DK', { weekday: 'long', day: 'numeric', month: 'long' }),
                        weekend: date.getDay() === 0 || date.getDay() === 6,
                        holiday: holidays.has(date.getTime()),
                        dayNumber: dayIndex + 1,
                        dayName: date.toLocaleDateString('da-DK', { weekday: 'short' }).slice(0, 2),
                        timerValue: null,
                        kodeValue: 0
                    };
                })
            };
        });
    }

    onTimerSaved(event: { timer: number }, day: any) {
        if (!this.canEdit()) { return; }
        const timerValue = event.timer;
        const selected = this.selectedMedarbejder();

        if (!selected) {
            return;
        }

        const existingEntry = this.normtiderStore.entries().find(e =>
            new Date(e.dato).toDateString() === day.date.toDateString()
        );

        const kodeToKeep = existingEntry?.kode || day.kodeValue || 0;

        this.normtiderStore.upsertEntry({
            mid: selected.cardId,
            date: day.date,
            timer: timerValue,
            kode: kodeToKeep
        });
    }

    async copyWeekday() {
        if (!this.canEdit()) { return; }
        const dayToCopy = this.selectedDay();
        const selected = this.selectedMedarbejder();

        if (!selected) {
            return;
        }

        if (!dayToCopy || dayToCopy.timerValue == null) {
            this.alertService.createAlert('Ingen dag valgt, vælg venligst en dag først.', 'warning');
            return;
        }

        const targetWeekday = dayToCopy.date.getDay();
        const valueToCopy = dayToCopy.timerValue;
        const updates: any[] = [];

        this.calendar().forEach(month => {
            month.days.forEach((day: any) => {
                const isSameWeekday = day.date.getDay() === targetWeekday;
                const isHoliday = this.isHoliday(day.date);
                const isOriginalDay = day.date.toDateString() === dayToCopy.date.toDateString();

                if (isSameWeekday && !isHoliday && !isOriginalDay) {
                    updates.push({
                        mid: selected.cardId,
                        date: day.date,
                        timer: valueToCopy
                    });
                }
            });
        });

        if (updates.length > 0) {
            this.copying.set(true);
            try {
                const confirmed = await this.confirmService.question(
                    `Kopier ${formatTime(valueToCopy)} til ${updates.length} øvrige ${this.selectedWeekday()} i ${this.currentYear()}? Eksisterende normtimer på disse dage erstattes. Hellig-/fridage undtages.`,
                    'Kopier normtimer', 'Annuller');
                if (confirmed) { await this.normtiderStore.bulkUpsert(updates); }
            } finally { this.copying.set(false); }
        }
    }

    removeFravaer(day: any, event: Event) {
        if (!this.canEdit()) { return; }
        event.stopPropagation();

        const selected = this.selectedMedarbejder();
        if (!selected) {
            return;
        }

        this.normtiderStore.upsertEntry({
            mid: selected.cardId,
            date: day.date,
            timer: day.timerValue || 0,
            kode: 0
        });
    }

    isHoliday(date: Date): boolean {
        const year = date.getFullYear();
        const holidays = this.getHolidays(year);

        return holidays.some(d =>
            d.getDate() === date.getDate() &&
            d.getMonth() === date.getMonth()
        );
    }

    getHolidays(year: number): Date[] {
        const easter = this.getEasterSunday(year);

        const addDays = (base: Date, days: number) => {
            const d = new Date(base);
            d.setDate(d.getDate() + days);
            return d;
        };

        return [
            new Date(year, 0, 1),   // Nytårsdag
            new Date(year, 5, 5),   // Grundlovsdag
            new Date(year, 11, 24), // Juleaften
            new Date(year, 11, 25), // Juledag
            new Date(year, 11, 26), // 2nd juledag
            new Date(year, 11, 31), // Nytårsaften

            addDays(easter, -3), // Skærtorsdag
            addDays(easter, -2), // Langefredag
            easter,              // Påskedag
            addDays(easter, 1),  // 2nd påskedag
            addDays(easter, 26), // Store bededag :)
            addDays(easter, 39), // Kristi himmelfart
            addDays(easter, 49), // Pinsedag
            addDays(easter, 50), // 2nd pinsedag
        ];
    }

    getEasterSunday(year: number): Date {
        const f = Math.floor;
        const G = year % 19;
        const C = f(year / 100);
        const H = (C - f(C / 4) - f((8 * C + 13) / 25) + 19 * G + 15) % 30;
        const I = H - f(H / 28) * (1 - f(29 / (H + 1)) * f((21 - G) / 11));
        const J = (year + f(year / 4) + I + 2 - C + f(C / 4)) % 7;
        const L = I - J;
        const month = 3 + f((L + 40) / 44);
        const day = L + 28 - 31 * f(month / 4);

        return new Date(year, month - 1, day);
    }

    onYearChange(newYear: number) {
        if (!Number.isInteger(newYear) || newYear < 1900 || newYear > 9999 || this.busy()) { return; }
        this.selectedDate.set(null);
        this.currentYear.set(newYear);
    }

    onUserChange(medarbejder: any) {
        if (this.busy()) { return; }
        this.selectedDate.set(null);
        this.selectedMedarbejder.set(medarbejder);
    }

    onDayClick(day: any) {
        this.selectedDate.set(day.key);
    }

    copyToOtherEmployee() {
        if (!this.canEdit()) { return; }
        const selected = this.selectedMedarbejder();

        if (!selected) {
            return;
        }

        const otherEmployees = this.activeUsers().filter(
            user => user.cardId !== selected.cardId
        );

        if (otherEmployees.length === 0) {
            this.alertService?.createAlert('Ingen andre medarbejdere fundet', 'info');
            return;
        }

        const modalRef = this.modalService.open(MedarbejderSelectorComponent, {
            size: 'md',
            backdrop: 'static'
        });

        modalRef.componentInstance.employees = otherEmployees;
        modalRef.result.then(async (selectedEmployeeIds) => {
            if (selectedEmployeeIds && selectedEmployeeIds.length > 0) {
                this.copying.set(true);
                try {
                    const count = this.normtiderStore.entries().length;
                    if (!count) { return; }
                    const confirmed = await this.confirmService.question(
                        `Kopier ${count} dages normtimer fra ${this.currentYear()} til ${selectedEmployeeIds.length} medarbejdere? Eksisterende værdier på de berørte datoer erstattes. Fravær kopieres ikke.`,
                        'Kopier normtimer', 'Annuller');
                    if (confirmed) { await this.performCopyToEmployees(selectedEmployeeIds); }
                } finally { this.copying.set(false); }
            }
        }, () => {});
    }

    performCopyToEmployees(selectedEmployeeIds: number[]) {
        const currentEntries = this.normtiderStore.entries();
        if (currentEntries.length === 0) { return; }

        const allUpdates: any[] = [];

        selectedEmployeeIds.forEach(targetEmployeeId => {
            currentEntries.forEach(entry => {
                allUpdates.push({
                    mid: targetEmployeeId,
                    dato: entry.dato,
                    timer: entry.timer,
                    kode: 0,
                    kontor: entry.kontor || 0
                });
            });
        });

        return this.normtiderStore.bulkUpsertMultipleUser(allUpdates);
    }

    async copyToNextYear() {
        if (!this.canEdit()) { return; }
        const selected = this.selectedMedarbejder();

        if (!selected) {
            return;
        }

        const nextYear = this.currentYear() + 1;
        const currentEntries = this.normtiderStore.entries();

        if (currentEntries.length === 0) {
            this.alertService?.createAlert('Ingen timer at kopiere fra nuværende år', 'info');
            return;
        }

        const weekTemplate = new Map();

        currentEntries.forEach(entry => {
            const entryDate = new Date(entry.dato);
            const weekNumber = this.getWeekNumber(entryDate);
            const weekday = entryDate.getDay();
            const weekKey = `${weekNumber}-${weekday}`;

            const isCurrentHoliday = this.isHoliday(entryDate);
            if (entry.timer !== null && entry.timer !== undefined && entry.timer !== 0 && !isCurrentHoliday) {
                weekTemplate.set(weekKey, entry.timer);
            }
        });

        const nextYearDates = this.getAllDatesInYear(nextYear);
        const updates: any[] = [];

        nextYearDates.forEach(date => {
            const weekNumber = this.getWeekNumber(date);
            const weekday = date.getDay();
            const weekKey = `${weekNumber}-${weekday}`;

            const isHolidayNextYear = this.isHoliday(date);

            if (weekTemplate.has(weekKey) && !isHolidayNextYear) {
                const timerValue = weekTemplate.get(weekKey);

                updates.push({
                    mid: selected.cardId,
                    date: date,
                    timer: timerValue,
                    kode: 0
                });
            }
        });

        if (updates.length === 0) {
            this.alertService?.createAlert('Ingen timer at kopiere til næste år', 'info');
            return;
        }

        this.copying.set(true);
        try {
            const existing = await this.normtiderService.getByUserAndYear(selected.cardId, nextYear).toPromise();
            const targetDates = new Set(updates.map(entry => entry.date.toDateString()));
            const overwritten = existing.filter(entry => targetDates.has(new Date(entry.dato).toDateString())).length;
            const confirmed = await this.confirmService.question(
                `Kopier normtimer til ${updates.length} datoer i ${nextYear}? ${overwritten} eksisterende registreringer på disse datoer erstattes, inklusive fravær. Øvrige datoer ændres ikke.`,
                'Kopier normtimer', 'Annuller');
            if (confirmed && await this.normtiderStore.bulkUpsert(updates)) {
                this.selectedDate.set(null);
                this.currentYear.set(nextYear);
            }
        } catch {
            this.alertService.createAlert('Næste års normtider kunne ikke hentes. Kopiering er ikke gennemført.', 'danger');
        } finally {
            this.copying.set(false);
        }
    }

    getWeekNumber(date: Date): number {
        const tempDate = new Date(date.valueOf());
        const dayOfWeek = (tempDate.getDay() + 6) % 7;
        tempDate.setDate(tempDate.getDate() - dayOfWeek + 3);
        const firstThursday = tempDate.valueOf();
        tempDate.setMonth(0, 1);
        if (tempDate.getDay() !== 4) {
            tempDate.setMonth(0, 1 + ((4 - tempDate.getDay()) + 7) % 7);
        }
        return 1 + Math.ceil((firstThursday - tempDate.valueOf()) / 604800000);
    }

    getAllDatesInYear(year: number): Date[] {
        const dates = [];
        const startDate = new Date(year, 0, 1);
        const endDate = new Date(year, 11, 31);

        for (const date = startDate; date <= endDate; date.setDate(date.getDate() + 1)) {
            dates.push(new Date(date));
        }
        return dates;
    }

    openFravaerModal() {
        if (!this.canEdit()) { return; }
        const selected = this.selectedMedarbejder();

        if (!selected) {
            return;
        }

        const modalRef = this.modalService.open(MedarbejdereFravaerPickerComponent, {
            size: 'lg',
            backdrop: 'static'
        });

        modalRef.componentInstance.activeYear = this.currentYear();

        modalRef.result.then((result) => {
            if (!result.fromDate || !result.toDate) {
                return;
            }

            const fromDate = new Date(result.fromDate);
            const toDate = new Date(result.toDate);
            const kode = result.kode;

            const datesToUpdate = this.getDatesInRange(fromDate, toDate);
            const updates: any[] = [];

            datesToUpdate.forEach(date => {
                let dayFound = null;
                for (const month of this.calendar()) {
                    const day = month.days.find((d: any) =>
                        d.date.toDateString() === date.toDateString()
                    );
                    if (day) {
                        dayFound = day;
                        break;
                    }
                }

                if (dayFound) {
                    const hasHours = dayFound.timerValue !== null &&
                        dayFound.timerValue !== undefined &&
                        dayFound.timerValue !== 0;

                    if (hasHours) {
                        updates.push({
                            mid: selected.cardId,
                            date: date,
                            timer: dayFound.timerValue,
                            kode: kode
                        });
                    }
                }
            });

            if (updates.length > 0) {
                this.normtiderStore.bulkUpsert(updates);
            } else {
                this.alertService?.createAlert('Ingen dage med timer fundet i det valgte periode', 'info');
            }

        }, () => {});
    }

    getDatesInRange(startDate: Date, endDate: Date): Date[] {
        const dates: Date[] = [];
        const currentDate = new Date(startDate);

        while (currentDate <= endDate) {
            dates.push(new Date(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
        }

        return dates;
    }

    onDismiss() {
        this.activeModal.dismiss();
    }
}
